import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Story Card Component
const StoryCard = ({ story }) => {
  return (
    <Link 
      to={`/stories/${story.slug}`}
      className="group block relative overflow-hidden"
    >
      {/* Background Image */}
      <div className="aspect-[4/5] sm:aspect-[3/4] relative overflow-hidden">
        {story.hero_image ? (
          <img 
            src={story.hero_image} 
            alt={story.client_name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
            <span className="font-serif text-4xl text-white/20">{story.client_name.charAt(0)}</span>
          </div>
        )}
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
        
        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <p className="text-white/50 text-xs tracking-[0.2em] uppercase mb-2">Success Story</p>
          <h3 className="font-serif text-2xl sm:text-3xl text-white mb-2">{story.client_name}</h3>
          <p className="text-white/60 text-sm line-clamp-2 mb-4">{story.title}</p>
          
          <div className="flex items-center gap-2 text-white/60 group-hover:text-white transition-colors">
            <span className="text-xs tracking-wider uppercase">Read Story</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </div>
    </Link>
  );
};

const StoriesPage = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/stories`);
      if (res.ok) {
        setStories(await res.json());
      }
    } catch (e) {
      console.error('Error fetching stories:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO 
        title="Success Stories - Our Work"
        description="Discover the stories behind our collaborations. From brand launches to creative campaigns, see how we bring visions to life."
        keywords="success stories, case studies, portfolio, creative work, brand stories"
        canonical="https://creativedirectorkhan.com/stories"
        jsonLd={stories.length > 0 ? {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Success Stories - CreativeDirectorKhan",
          "description": "Discover the stories behind our collaborations. From brand launches to creative campaigns, see how we bring visions to life.",
          "url": "https://creativedirectorkhan.com/stories",
          "mainEntity": {
            "@type": "ItemList",
            "itemListElement": stories.map((story, idx) => ({
              "@type": "ListItem",
              "position": idx + 1,
              "url": `https://creativedirectorkhan.com/stories/${story.slug}`,
              "name": `${story.client_name} - ${story.title}`
            }))
          }
        } : undefined}
      />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-white/40 text-xs tracking-[0.3em] uppercase mb-6">Our Work</p>
            <h1 className="font-serif text-5xl md:text-7xl font-light text-white leading-[0.95] mb-6">
              Success
              <span className="block text-white/40 italic">Stories</span>
            </h1>
            <p className="text-white/60 text-lg max-w-xl leading-relaxed">
              Every project has a story. Every client has a vision. Here&apos;s how we brought them together.
            </p>
          </div>
        </div>
      </section>

      {/* Stories Grid */}
      <section className="px-6 md:px-12 pb-20">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-white/40">Loading stories...</p>
            </div>
          ) : stories.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {stories.map(story => (
                <StoryCard key={story.slug} story={story} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 border border-white/10">
              <p className="text-white/40 mb-2">No stories yet</p>
              <p className="text-white/30 text-sm">Check back soon for our success stories</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 md:px-12 py-20 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
            Want to be our next success story?
          </h2>
          <p className="text-white/50 mb-8 max-w-xl mx-auto">
            Let&apos;s create something extraordinary together. Your vision, our expertise.
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black text-sm tracking-wider uppercase hover:bg-white/90 transition-colors"
          >
            Start Your Story
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default StoriesPage;
